import { useMemo, useState } from 'react';
import { type ScheduleItem, type ScheduleLinkType } from '@/lib/scheduleUtils';

export type PendingScheduleLinkDraft = {
  direction: 'predecessor' | 'successor';
  otherItemId: string;
  link_type: ScheduleLinkType;
  lag_days: number;
};

interface Props {
  scheduleItems: ScheduleItem[];
  draft: PendingScheduleLinkDraft | null;
  onChange: (draft: PendingScheduleLinkDraft | null) => void;
  disabled?: boolean;
}

const ACTIVE_TYPES = ['FS', 'SS'] as const;

export default function ScheduleItemModalCreateLinkSection({
  scheduleItems,
  draft,
  onChange,
  disabled,
}: Props) {
  const [enabled, setEnabled] = useState(!!draft);
  const [search, setSearch] = useState('');

  const localDraft: PendingScheduleLinkDraft = draft ?? {
    direction: 'predecessor',
    otherItemId: '',
    link_type: 'FS',
    lag_days: 0,
  };

  const availableItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scheduleItems.filter((item) => {
      if (!q) return true;
      return `${item.task_no ?? ''} ${item.title}`.toLowerCase().includes(q);
    });
  }, [scheduleItems, search]);

  const updateDraft = (patch: Partial<PendingScheduleLinkDraft>) => {
    const next = { ...localDraft, ...patch };
    onChange(next.otherItemId ? next : null);
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer text-sm text-[#374151]">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => {
            const on = e.target.checked;
            setEnabled(on);
            if (!on) onChange(null);
            else onChange(localDraft.otherItemId ? localDraft : { ...localDraft, otherItemId: '' });
          }}
          className="w-4 h-4 accent-[#E8621A]"
        />
        <span className="font-semibold">Add dependency link when creating this task</span>
      </label>

      {enabled && (
        <div className="bg-[#F9FAFB] rounded-xl p-3 space-y-3 border border-gray-100">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => updateDraft({ direction: 'predecessor' })}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 cursor-pointer ${
                localDraft.direction === 'predecessor'
                  ? 'border-[#E8621A] bg-[#FEF0E7] text-[#E8621A]'
                  : 'border-gray-200 text-[#6B7280]'
              }`}
            >
              Has predecessor
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => updateDraft({ direction: 'successor' })}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 cursor-pointer ${
                localDraft.direction === 'successor'
                  ? 'border-[#E8621A] bg-[#FEF0E7] text-[#E8621A]'
                  : 'border-gray-200 text-[#6B7280]'
              }`}
            >
              Has successor
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1.5">
              {localDraft.direction === 'predecessor' ? 'Predecessor task' : 'Successor task'}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search task name..."
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2 text-sm mb-2"
            />
            <select
              value={localDraft.otherItemId}
              disabled={disabled}
              onChange={(e) => updateDraft({ otherItemId: e.target.value })}
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm"
            >
              <option value="">Choose an item...</option>
              {availableItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.task_no ?? '-'} — {item.title}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {ACTIVE_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                disabled={disabled}
                onClick={() => updateDraft({ link_type: type })}
                className={`py-2 rounded-xl border-2 text-xs font-bold cursor-pointer ${
                  localDraft.link_type === type
                    ? 'border-[#E8621A] bg-[#FEF0E7] text-[#E8621A]'
                    : 'border-gray-100 text-[#374151]'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1.5">Lag days</label>
            <input
              type="number"
              min={-365}
              max={365}
              disabled={disabled}
              value={localDraft.lag_days}
              onChange={(e) => updateDraft({ lag_days: Number(e.target.value) || 0 })}
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
